/**
 * Identidade de uma estação de roaming (OCPI), como o parceiro a identifica.
 *
 * O `stationID` de uma estação OCPI é `base64("OCPI:<location_id>:<evse_uid>")` e
 * não carrega a party, enquanto `location_id` é único apenas *por party*. Duas
 * parties que publiquem o mesmo EVSE geram o mesmo `stationID` e passam a dividir
 * um único documento espelhado, cujo dono muda conforme a última sincronização.
 * Por isso a campanha guarda `(partyId, locationId, evseUid)` para roaming, em vez
 * do id do documento.
 */
export const decodeOcpiStationId = (stationID) => {
  if (!stationID) return undefined;

  try {
    const decoded = atob(stationID);
    if (!decoded.includes("OCPI:")) return undefined;

    // Só os dois primeiros separadores são estruturais: um `evse_uid` pode
    // conter `:`, e um split simples o truncaria, gravando uma identidade que
    // nunca casa — e que diverge do backend e do eMSP.
    const [, locationId, ...evseParts] = decoded.split(":");
    const evseUid = evseParts.join(":");
    if (!locationId || !evseUid) return undefined;

    return { locationId, evseUid };
  } catch (error) {
    return undefined;
  }
};

export const isOcpiStation = (station) =>
  Boolean(decodeOcpiStationId(station?.stationID));

export const buildOcpiStationKey = ({ partyId, locationId, evseUid }) =>
  `${String(partyId).toUpperCase()}:${locationId}:${evseUid}`;

export const getStationOcpiKey = (station, partyId) => {
  const decoded = decodeOcpiStationId(station?.stationID);
  if (!decoded || !partyId) return undefined;

  return buildOcpiStationKey({ partyId, ...decoded });
};

/**
 * A estação do catálogo é a identidade `ocpiStation`?
 *
 * A party tem que vir da ESTAÇÃO (fallback: a empresa da entrada), nunca da
 * identidade que se está procurando. Comparar usando a party da identidade faz
 * qualquer estação com o mesmo `(location, evse)` casar, e é exatamente esse o
 * caso ambíguo: duas parties publicando o mesmo EVSE dividem um documento
 * espelhado. Casar assim resolveria a identidade de uma party no documento da
 * outra e, ao salvar, reescreveria a party da restrição.
 */
export const stationMatchesOcpiIdentity = (
  station,
  ocpiStation,
  fallbackPartyId
) => {
  const stationPartyId = station?.ocpiPartyId ?? fallbackPartyId;
  if (!stationPartyId) return false;

  return (
    getStationOcpiKey(station, stationPartyId) ===
    buildOcpiStationKey(ocpiStation)
  );
};
